// Where a text field's text sits inside the field's box.
//
// Until the constructor's toolbar was made to float (Aug 2026), a text field on the
// canvas carried two things inside its own box, above the text: the "Show/Hide Editor"
// button row (~39px) and the Quill toolbar (42-138px, by field width). So the text an
// author placed started that far below the `top` the field was saved with, and the
// filled form and the print added the same amount back to land the text where the
// author had put it.
//
// The floating toolbar takes neither out of the box any more, so a field authored now
// shows its text exactly at its stored `top`. Fields saved before that carry no
// `floatingToolbar` flag, and they keep the old geometry on every surface - canvas,
// filled form, print - so forms built back then look exactly as they always did.
export const hasFloatingToolbar = (element) => !!element?.options?.floatingToolbar;

// The button row above the field, in grid units.
export const LEGACY_TEXT_BUTTON_ROW_SHIFT = 3.9;

// The toolbar's own height for a given rendered field width, in grid units: the toolbar
// wraps onto more rows the narrower the field is (the .quill-lg/md/sm/xs breakpoints in
// editor.scss). Below 200px the old code left the shift untouched, so this returns null
// and the caller keeps whatever it had.
export const legacyTextToolbarShift = (renderedWidth) => {
  if (!renderedWidth || renderedWidth >= 661) return 4.2;
  if (renderedWidth >= 343) return 6.6;
  if (renderedWidth >= 272) return 9;
  if (renderedWidth >= 200) return 13.8;

  return null;
};

// The canvas and the rendered form are both a fixed 1100px-wide grid, so a field's
// width option - a percentage - maps to a known pixel width. The printed report makes
// the same mapping in form_builder/utils.py.
export const FORM_AREA_WIDTH = 1100;

// The toolbar an older field was authored with, in grid units, read off the width the
// field has on the grid.
const storedToolbarShift = (options = {}) => {
  const width = Number(options.width);
  const renderedWidth = Number.isFinite(width) ? (width / 100) * FORM_AREA_WIDTH : 0;

  return legacyTextToolbarShift(renderedWidth) ?? 0;
};

// How far below its stored `top` an older field shows its text, in grid units: the
// button row plus the toolbar the field was authored with.
export const legacyTextTopShift = (options = {}) => LEGACY_TEXT_BUTTON_ROW_SHIFT + storedToolbarShift(options);

// The shortest a text area is allowed to get, matching the floor the constructor's own
// resize handle keeps (from-builder/elements/text-element.js).
const MIN_INNER_ELEM_HEIGHT = 11;

// Puts a form's text fields on the floating-toolbar layout, for the constructor to open
// it with: the box moves down to where its text already is, so the text does not budge -
// on the canvas, on the rendered form or in print - while the room the button row and
// the toolbar held above it inside the box is gone. That room is what the toolbar had to
// keep occupying even while hidden, and what left a gap over the text when it was.
//
// Fields already on that layout are left as they are, and so is the form itself: this
// returns a new array, and reaches the stored form only if the author saves it, which
// changes nothing about how the form draws either way.
export const withFloatingToolbarTextFields = (fields) => {
  if (!Array.isArray(fields)) return fields;

  return fields.map((field) => {
    if (field?.type !== 'text' || hasFloatingToolbar(field)) return field;

    const options = field.options ?? {};

    return {
      ...field,
      options: {
        ...options,
        top: (options.top ?? 0) + legacyTextTopShift(options),
        // `innerElemHeight` covered the toolbar as well as the text; now that the
        // toolbar is out of the box it covers the text alone, so the writing area keeps
        // the size and the place it had. The field's own `height` - the footprint the
        // rendered form and the print lay out from - is left untouched.
        innerElemHeight: Math.max(MIN_INNER_ELEM_HEIGHT, (options.innerElemHeight ?? 0) - storedToolbarShift(options)),
        floatingToolbar: true,
      },
    };
  });
};
